/**
 * AppShellSkeleton
 *
 * Lightweight above-the-fold shell painted while GlobalState initializes
 * (profile/workspace bootstrap). Replaces the full-screen spinner so a large,
 * stable LCP element paints before hydration/async init completes.
 *
 * Primary responsibilities:
 * - Paint the chat-shaped layout frame (sidebar rail, header, message area,
 *   input affordance) as plain static markup
 * - Stay hydration-safe: no hooks, no browser APIs, no opacity-from-0 on the
 *   LCP element, identical server/client markup
 *
 * Dependencies:
 * - cn (class merge)
 */

import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface AppShellSkeletonProps {
  /** Render the chat-shaped frame (sidebar + message area + input). */
  variant?: 'chat' | 'plain'
  /**
   * Real interactive composer rendered in place of the fake
   * skeleton input bar. The skeleton frame itself stays inert
   * (pointer-events-none) so clicks on decorative bars never
   * register as dead clicks on a pretend textarea.
   */
  composerSlot?: ReactNode
  /** Loading has exceeded the slow threshold (>10s). */
  isSlow?: boolean
}

// --------------

// A real, large text/structural element paints immediately so the browser has
// a stable LCP candidate. It is intentionally NOT animated from opacity:0.
export function AppShellSkeleton({
  variant = 'chat',
  composerSlot,
  isSlow = false
}: AppShellSkeletonProps) {
  if (variant === 'plain') {
    return (
      <div className="bg-background pointer-events-none flex size-full flex-col">
        <div className="border-border/60 flex h-14 w-full items-center gap-3 border-b px-4">
          <div className="bg-muted size-7 rounded-md" />
          <div className="bg-muted h-4 w-32 rounded" />
        </div>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="bg-muted/60 h-40 w-full max-w-2xl rounded-2xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background pointer-events-none flex size-full">
      {/* Sidebar rail */}
      <aside
        aria-hidden="true"
        className="border-border/60 hidden w-64 shrink-0 flex-col gap-3 border-r p-3 md:flex"
      >
        <div className="bg-muted h-9 w-full rounded-lg" />
        <div className="mt-2 flex flex-col gap-2">
          {SIDEBAR_ROWS.map((width, i) => (
            <div
              key={i}
              className={cn('bg-muted/70 h-7 rounded-md', width)}
            />
          ))}
        </div>
      </aside>

      {/* Main chat column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <header
          aria-hidden="true"
          className="border-border/60 flex h-14 items-center gap-3 border-b px-4"
        >
          <div className="bg-muted size-8 rounded-full" />
          <div className="bg-muted h-4 w-40 rounded" />
        </header>

        {/* Message area — the large LCP candidate */}
        <main className="flex flex-1 flex-col justify-end gap-6 px-4 py-8 sm:px-8">
          <div className="mx-auto w-full max-w-3xl">
            <h1 className="text-foreground/90 text-2xl font-semibold tracking-tight sm:text-3xl">
              Loading your workspace
            </h1>
            <p className="text-muted-foreground mt-2 text-sm" role="status">
              {isSlow
                ? 'Still loading — this is taking longer than usual…'
                : 'Setting up your chat and tools…'}
            </p>
          </div>
          <div
            aria-hidden="true"
            className="mx-auto flex w-full max-w-3xl flex-col gap-4"
          >
            <div className="bg-muted/60 h-20 w-3/4 animate-pulse rounded-2xl" />
            <div className="bg-muted/40 ml-auto h-16 w-1/2 animate-pulse rounded-2xl" />
            <div className="bg-muted/60 h-24 w-2/3 animate-pulse rounded-2xl" />
          </div>
        </main>

        {/* Input affordance: a real composer when provided, otherwise
            an obviously-skeleton bar (pulsing, aria-hidden, inert). */}
        <div className="px-4 pb-6 sm:px-8">
          {composerSlot ?? (
            <div
              aria-hidden="true"
              data-testid="app-shell-skeleton-input"
              className="border-border/60 bg-muted/30 mx-auto flex h-14 w-full max-w-3xl animate-pulse items-center rounded-2xl border px-4"
            >
              <div className="bg-muted h-4 w-48 rounded" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const SIDEBAR_ROWS = ['w-5/6', 'w-2/3', 'w-3/4', 'w-1/2', 'w-4/5', 'w-3/5']
